import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { LayoutService } from '@shared/services/layout/layout.service';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { BrowserStorageService } from '@core/services/browser-storage/browser-storage.service';
import { PortfolioStateService } from '@shared/services/portfolio/portfolio-state.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { ErrorShownParams } from '@core/services/analytics/analytics.model';
import { getFreshAccessToken } from './token-refresh.util';
import { notifyLocalizedError } from './http-error-messages.util';

export const httpResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const layoutService = inject(LayoutService);
  const router = inject(Router);
  const authenticationService = inject(AuthenticationService);
  const browserStorageService = inject(BrowserStorageService);
  const portfolioStateService = inject(PortfolioStateService);
  const analyticsService = inject(AnalyticsService);
  const platformId = inject(PLATFORM_ID);

  const bucketErrorType = (status: number): ErrorShownParams['errorType'] => {
    if (status === 401 || status === 403) return 'auth';
    if (status === 429) return 'network';
    if (status >= 500) return 'server';
    if (status === 409) return 'duplicate';
    if (status >= 400) return 'validation';
    return 'other';
  };

  const redirectToLogin = () => {
    const refreshToken = browserStorageService.get('refreshToken') || '';
    // Try to revoke refresh token on server if we're not already in a logout request
    if (refreshToken && !req.url.includes('/logout')) {
      authenticationService.logout().subscribe();
    } else {
      // logout() resets analytics identity itself; this path would otherwise skip it.
      analyticsService.resetUser();
    }
    // Immediately clean up local state regardless of server response
    authenticationService.currentUser.set(null);
    browserStorageService.remove('accessToken');
    browserStorageService.remove('refreshToken');
    // Reset portfolio state to prevent data leakage between users
    portfolioStateService.resetState();
    router.navigate(['/auth/login']);
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!isPlatformBrowser(platformId)) return throwError(() => error);

      if (error.status === 401 || error.status === 403) {
        // A 401 means the token itself is invalid/expired -> refresh-fail or retry-fail both log out.
        // A 403 means the request was authenticated but denied -> the token's authorization state
        // (e.g. subscription tier) may just be stale, so a retry-fail only surfaces a message,
        // it never forces a logout.
        const isForbidden = error.status === 403;
        const refreshToken = browserStorageService.get('refreshToken') || '';

        if (!refreshToken) {
          if (isForbidden) {
            notifyLocalizedError(layoutService, browserStorageService, 'forbidden');
            analyticsService.trackErrorShown({
              httpStatus: error.status,
              errorType: 'auth',
              screenName: router.url,
            });
          } else {
            redirectToLogin();
          }
          return throwError(() => error);
        }

        return getFreshAccessToken(authenticationService, refreshToken).pipe(
          switchMap((token) => {
            // Resync permissions/subscription now that the token is fresh
            if (isForbidden) {
              authenticationService.getUserProfile().subscribe();
            }

            const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
            return next(retryReq).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (isForbidden) {
                  // Retried with a fresh token and still forbidden -> genuine permission denial
                  notifyLocalizedError(layoutService, browserStorageService, 'forbidden');
                  analyticsService.trackErrorShown({
                    httpStatus: retryError.status,
                    errorType: 'auth',
                    screenName: router.url,
                  });
                } else {
                  redirectToLogin();
                }
                return throwError(() => retryError);
              })
            );
          }),
          catchError((refreshError: HttpErrorResponse) => {
            // The refresh-token call itself failed -> the session is actually invalid
            redirectToLogin();
            return throwError(() => refreshError);
          })
        );
      }

      if (error.status === 429) {
        notifyLocalizedError(layoutService, browserStorageService, 'tooManyRequests');
        analyticsService.trackErrorShown({
          httpStatus: error.status,
          errorType: 'network',
          screenName: router.url,
        });
      } else {
        layoutService.$notifyToaster.next({
          text: error.error ? error.error.message : error.message,
          type: 'error',
        });
        analyticsService.trackErrorShown({
          httpStatus: error.status,
          errorType: bucketErrorType(error.status),
          screenName: router.url,
        });
      }

      return throwError(() => error);
    })
  );
};
